import type { StorePinData, StoreEventHandlers } from './storeTypes';
import type { MappedShapeData } from './ShapeMapper.svelte';
import { debugLog } from './debugUtils';
import { highlightShape, unhighlightShape } from './shapeUtils';

/**
 * Get pin size - simplified to use a single scale for all pins
 */
function getPinSize(pinConfig: any): number {
  return pinConfig.scale || 1.3;
}

/**
 * Creates pins for a floor in the SVG
 */
export function createPinsForFloor(
  svgElement: SVGSVGElement,
  pins: StorePinData[],
  onStoreClick: (storeId: string) => void,
  onStoreHover: (storeId: string | null) => void,
  eventHandlers: Map<string, StoreEventHandlers>,
  highlightedStoreId: string | null,
  enableStoreClick: boolean,
  enableStoreHover: boolean,
  pinConfig: any
): void {
  if (!svgElement) return;
  
  debugLog(`Creating ${pins.length} pins for current floor`, 'floorChanges');
  
  // Create or get pin overlay group
  let pinGroup = svgElement.querySelector('.pin-overlays') as SVGGElement;
  if (!pinGroup) {
    pinGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    pinGroup.classList.add('pin-overlays');
    svgElement.appendChild(pinGroup);
  }
  
  // Create pins
  pins.forEach(pin => {
    // Create a group for this pin and its label
    const pinContainer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    pinContainer.classList.add('pin-container');
    pinContainer.setAttribute('data-store-id', pin.id);
    
    // Add shape ID if we found a matching shape
    if (pin.shapeId) {
      pinContainer.setAttribute('data-shape-id', pin.shapeId);
      pinContainer.classList.add('has-shape');
    }
    
    // Create pin element using a path for a map pin icon
    const pinElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const pinPath = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';
    pinElement.setAttribute('d', pinPath);
    pinElement.setAttribute('fill', pinConfig.fillColor.inactive);
    pinElement.setAttribute('stroke', pinConfig.strokeColor.inactive);
    pinElement.setAttribute('stroke-width', pinConfig.strokeWidth.inactive.toString());
    pinElement.setAttribute('data-store-id', pin.id);
    pinElement.classList.add('store-pin');
    pinElement.style.transition = 'all 0.2s ease-in-out';
    
    // Position the pin so its tip is at the (x, y) coordinates
    // The pin is 24 units high, with the tip at the bottom center.
    // We scale it to a reasonable size and translate it.
    // Use simplified single scale for all pins
    const scale = getPinSize(pinConfig);
    const pinHeight = 24 * scale;
    const pinWidth = 24 * scale;
    
    // Offset the pin upwards slightly so it sits above the label
    const yOffset = pinConfig.yOffset || 0;
    
    pinElement.setAttribute('transform', `translate(${pin.x - (pinWidth / 2)}, ${pin.y - pinHeight + yOffset}) scale(${scale})`);
    
    // Store the base scale for later use in highlight/reset functions
    pinElement.setAttribute('data-base-scale', scale.toString());
    
    // Create handlers for this pin
    const handlers = {
      click: (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        debugLog(`Pin clicked: ${pin.id} (${pin.name})`, 'floorChanges');
        
        onStoreClick(pin.id);
      },
      mouseenter: (e: MouseEvent) => {
        e.stopPropagation();
        // If the pin has a matching shape, highlight that shape too
        if (pin.shapeId) {
          if (pin.id !== highlightedStoreId) {
          highlightShape(svgElement, pin.shapeId, pin);
          }
        }
        onStoreHover(pin.id);
      },
      mouseleave: (e: MouseEvent) => {
        e.stopPropagation();
        // If the pin has a matching shape, remove highlight
        if (pin.shapeId) {
          if (pin.id !== highlightedStoreId) {
          unhighlightShape(svgElement, pin.shapeId, pin);
          }
        }
        onStoreHover(null);
      }
    };
    
    // Add event listeners only if enabled
    if (enableStoreClick) {
    pinElement.addEventListener('click', handlers.click as EventListener, true);
    }
    if (enableStoreHover) {
    pinElement.addEventListener('mouseenter', handlers.mouseenter as EventListener, true);
    pinElement.addEventListener('mouseleave', handlers.mouseleave as EventListener, true);
    }
    
    // Store handlers for cleanup
    eventHandlers.set(pin.id, handlers);
    
    // Add elements to the container
    pinContainer.appendChild(pinElement);
    
    // Store reference to element
    pin.element = pinElement;
    
    // Add to group
    pinGroup.appendChild(pinContainer);
  });
}

/**
 * Highlights a store pin
 */
export function highlightStorePin(
  svgElement: SVGSVGElement,
  container: Element,
  storeId: string,
  pinConfig: any
): void {
  const pinElement = container.querySelector('.store-pin');
  if (!pinElement) return;
  
  // Set CSS variables for colors that can be used by the hover-highlighted class
  (container as HTMLElement).style.setProperty('--highlight-color', pinConfig.fillColor.active);
  (container as HTMLElement).style.setProperty('--highlight-stroke-color', pinConfig.strokeColor.active);
  
  // Update the pin styling - apply to the specific element only
  (pinElement as SVGElement).setAttribute('fill', pinConfig.fillColor.active);
  (pinElement as SVGElement).setAttribute('stroke', pinConfig.strokeColor.active);
  (pinElement as SVGElement).setAttribute('stroke-width', pinConfig.strokeWidth.active.toString());
  
  // Apply drop shadow only to this pin element, not globally
  (pinElement as SVGElement).classList.add('highlighted');
  
  // Get the base scale and apply the active multiplier
  const baseScale = parseFloat((pinElement as SVGElement).getAttribute('data-base-scale') || '1.3');
  const activeMultiplier = pinConfig.activeSizeMultiplier || 1.2;
  const scale = baseScale * activeMultiplier;
  
  const currentTransform = (pinElement as SVGElement).getAttribute('transform') || '';
  const baseTransform = currentTransform.split('scale')[0];
  (pinElement as SVGElement).setAttribute('transform', `${baseTransform} scale(${scale})`);
  
  container.classList.add('highlighted');
}

/**
 * Resets a store pin to default style
 */
export function resetStorePin(
  svgElement: SVGSVGElement,
  container: Element,
  pinConfig: any
): void {
  const pinElement = container.querySelector('.store-pin');
  if (!pinElement) return;
  
  // Reset to default
  (pinElement as SVGElement).setAttribute('fill', pinConfig.fillColor.inactive);
  (pinElement as SVGElement).setAttribute('stroke', pinConfig.strokeColor.inactive);
  (pinElement as SVGElement).setAttribute('stroke-width', pinConfig.strokeWidth.inactive.toString());
  
  // Reset scale to base scale
  const baseScale = parseFloat((pinElement as SVGElement).getAttribute('data-base-scale') || '1.3');
  const currentTransform = (pinElement as SVGElement).getAttribute('transform') || '';
  const baseTransform = currentTransform.split('scale')[0];
  (pinElement as SVGElement).setAttribute('transform', `${baseTransform} scale(${baseScale})`);
  
  // Remove the highlighted class to reset drop shadow
  (pinElement as SVGElement).classList.remove('highlighted');
  (pinElement as SVGElement).classList.remove('hover-highlighted');
  
  container.classList.remove('highlighted');
}

/**
 * Clears all pins from the SVG
 */
export function clearAllPins(
  svgElement: SVGSVGElement,
  eventHandlers: Map<string, StoreEventHandlers>
): void {
  if (!svgElement) return;
  
  debugLog('Clearing all pins and shape event handlers', 'floorChanges');
  
  // Remove pin overlay group
  const pinGroup = svgElement.querySelector('.pin-overlays');
  if (pinGroup) {
    // Find all pin containers first
    const pinContainers = pinGroup.querySelectorAll('.pin-container');
    
    // Remove event listeners from pins
    pinContainers.forEach(container => {
      const storeId = container.getAttribute('data-store-id');
      if (storeId && eventHandlers.has(storeId)) {
        const pinElement = container.querySelector('.store-pin');
        if (pinElement) {
          try {
            const handlers = eventHandlers.get(storeId)!;
            pinElement.removeEventListener('click', handlers.click as EventListener, true);
            pinElement.removeEventListener('mouseenter', handlers.mouseenter as EventListener, true);
            pinElement.removeEventListener('mouseleave', handlers.mouseleave as EventListener, true);
          } catch (e) {
            // Ignore errors during cleanup
          }
        }
      }
    });
    
    // Clear the group
    try {
      while (pinGroup.firstChild) {
        pinGroup.removeChild(pinGroup.firstChild);
      }
    } catch (e) {
      // SVG might have been removed, just clear our references
      debugLog('Error clearing pin group, SVG may have been removed', 'floorChanges');
    }
  }
  
  // Remove event listeners from shapes
  try {
    const mappedShapes = svgElement.querySelectorAll('.mapped-store-shape');
    mappedShapes.forEach(shape => {
      const shapeId = shape.id;
      const handlerKey = `shape_${shapeId}`;
      
      if (eventHandlers.has(handlerKey)) {
        try {
          const handlers = eventHandlers.get(handlerKey)!;
          shape.removeEventListener('click', handlers.click as EventListener, true);
          shape.removeEventListener('mouseenter', handlers.mouseenter as EventListener, true);
          shape.removeEventListener('mouseleave', handlers.mouseleave as EventListener, true);
          
          // Remove the mapping attributes and classes
          shape.removeAttribute('data-store-id');
          shape.removeAttribute('data-store-name');
          shape.classList.remove('mapped-store-shape');
          shape.classList.remove('highlighted-shape');
          
          // Reset any style changes
          shape.removeAttribute('data-original-fill');
          shape.removeAttribute('data-original-stroke');
          shape.removeAttribute('data-original-stroke-width');
          shape.removeAttribute('fill-opacity');
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
    });
  } catch (e) {
    debugLog('Error cleaning up mapped shapes, SVG may have been removed', 'floorChanges');
  }
  
  // Clean up overlays
  try {
    const overlays = svgElement.querySelectorAll('.shape-highlight-overlay');
    overlays.forEach(overlay => {
      overlay.parentNode?.removeChild(overlay);
    });
  } catch (e) {
    // Ignore errors during cleanup
  }
  
  // Clear event handlers
  eventHandlers.clear();
}

 